import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { AdminPageShell } from '../components/AdminShared';
import { toast } from 'sonner';
import { Search, Trash2, Eye, RefreshCw } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-emerald-100 text-emerald-800',
  converted: 'bg-green-100 text-green-800',
  closed: 'bg-slate-100 text-slate-600',
};
const SOURCE_LABELS = { demo: 'Book Demo', booking: 'Book Interpreter', contact: 'Contact' };
const STATUSES = ['new', 'contacted', 'qualified', 'converted', 'closed'];

export default function AdminLeadsPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterSource) params.set('source', filterSource);
    if (search) params.set('search', search);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setLeads(await res.json());
    } catch (_) {}
    setLoading(false);
  }, [token, filterStatus, filterSource, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateLead = async (id, data) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        const updated = await res.json();
        setLeads(prev => prev.map(l => l.id === id ? updated : l));
        if (selected?.id === id) setSelected(updated);
        toast.success('Lead updated');
      }
    } catch (_) { toast.error('Failed to update lead'); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(prev => prev.filter(l => l.id !== id));
      setSelected(null);
      toast.success('Lead deleted');
    } catch (_) { toast.error('Failed to delete'); }
  };

  const openDetail = (lead) => {
    setSelected(lead);
    setNotes(lead.notes || '');
  };

  const counts = { total: leads.length };
  STATUSES.forEach(s => { counts[s] = leads.filter(l => l.status === s).length; });

  return (
    <AdminPageShell title="Leads" testId="admin-leads-page" actions={
      <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="leads-refresh">
        <RefreshCw className="w-4 h-4 mr-1" />Refresh
      </Button>
    }>
      <div className="space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-slate-900">{counts.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
        {STATUSES.map(s => (
          <div key={s} className="bg-white border rounded-lg p-3 text-center cursor-pointer hover:ring-1 hover:ring-blue-400" onClick={() => setFilterStatus(filterStatus === s ? '' : s)}>
            <p className="text-2xl font-bold text-slate-900">{counts[s]}</p>
            <p className="text-xs text-slate-500 capitalize">{s}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search name, email, company..." className="pl-9 bg-white text-slate-900 border-slate-300" value={search} onChange={e => setSearch(e.target.value)} data-testid="leads-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="leads-filter-status">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="leads-filter-source">
          <option value="">All Sources</option>
          <option value="demo">Book Demo</option>
          <option value="booking">Book Interpreter</option>
          <option value="contact">Contact</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-900">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-700">Name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-700">Email</th>
                <th className="text-left px-4 py-3 font-medium text-slate-700">Company</th>
                <th className="text-left px-4 py-3 font-medium text-slate-700">Source</th>
                <th className="text-left px-4 py-3 font-medium text-slate-700">Status</th>
                <th className="text-left px-4 py-3 font-medium text-slate-700">Date</th>
                <th className="text-right px-4 py-3 font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">No leads found</td></tr>
              ) : leads.map(lead => (
                <tr key={lead.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(lead)} data-testid={`lead-row-${lead.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{lead.name || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{lead.email || '-'}</td>
                  <td className="px-4 py-3 text-slate-900">{lead.company || '-'}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-xs text-slate-700 border-slate-300">{SOURCE_LABELS[lead.source] || lead.source}</Badge></td>
                  <td className="px-4 py-3">
                    <select value={lead.status} onClick={e => e.stopPropagation()} onChange={e => updateLead(lead.id, { status: e.target.value })} className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${STATUS_COLORS[lead.status]}`} data-testid={`lead-status-${lead.id}`}>
                      {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(lead.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openDetail(lead); }} data-testid={`lead-view-${lead.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); deleteLead(lead.id); }} data-testid={`lead-delete-${lead.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4" data-testid="lead-detail-dialog">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-slate-500 text-xs">Name</p><p className="font-medium">{selected.name}</p></div>
                <div><p className="text-slate-500 text-xs">Email</p><p className="font-medium">{selected.email}</p></div>
                <div><p className="text-slate-500 text-xs">Company</p><p className="font-medium">{selected.company || '-'}</p></div>
                <div><p className="text-slate-500 text-xs">Phone</p><p className="font-medium">{selected.phone || '-'}</p></div>
                <div><p className="text-slate-500 text-xs">Source</p><Badge variant="outline" className="text-slate-700 border-slate-300">{SOURCE_LABELS[selected.source] || selected.source}</Badge></div>
                <div><p className="text-slate-500 text-xs">Status</p>
                  <select value={selected.status} onChange={e => updateLead(selected.id, { status: e.target.value })} className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${STATUS_COLORS[selected.status]}`}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><p className="text-slate-500 text-xs">Date</p><p className="font-medium">{new Date(selected.created_at).toLocaleString()}</p></div>
              </div>
              {selected.message && (
                <div><p className="text-slate-500 text-xs mb-1">Message</p><p className="text-sm bg-slate-50 text-slate-900 rounded-lg p-3">{selected.message}</p></div>
              )}
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div>
                  <p className="text-slate-500 text-xs mb-1">Additional Info</p>
                  <div className="bg-slate-50 text-slate-900 rounded-lg p-3 text-sm space-y-1">
                    {Object.entries(selected.metadata).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="text-slate-500">{k.replace(/_/g, ' ')}</span><span className="font-medium">{String(v)}</span></div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Admin Notes</Label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-900 resize-none" placeholder="Internal notes about this lead..." />
                <Button size="sm" className="mt-2" onClick={() => updateLead(selected.id, { notes })} data-testid="lead-save-notes">Save Notes</Button>
              </div>
              <div className="flex justify-end">
                <Button variant="destructive" size="sm" onClick={() => deleteLead(selected.id)} data-testid="lead-detail-delete"><Trash2 className="w-4 h-4 mr-1" />Delete Lead</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </AdminPageShell>
  );
}
